/* eslint complexity: ["error", {max: 10}] -- Due to long file */
/* eslint max-lines: ["error", {max: 385}] -- Due to long file */
/* eslint max-lines-per-function: ["error", {max: 195}] -- Due to long file */
/* eslint max-statements: ["error", {max: 26}] -- Due to complex logic */
import { useCallback, useState } from "react";

import isNil from "lodash/isNil";
import { Alert } from "react-native";
import { useSelector } from "react-redux";

import {
  AnalyticsComponent,
  AnalyticsFieldName,
} from "@/constants/analytics/Analytics";
import {
  ScheduleAnalyticsEvent,
  ScheduleAnalyticsFieldName,
  ScheduleAnalyticsSources,
  WaitingRoomAnalyticsEvent,
} from "@/constants/analytics/Scheduling";
import { ScheduleTestID } from "@/constants/TestID";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useWaitingRoomIntercept from "@/hooks/useWaitingRoomIntercept";
import { useAnalytics } from "@/services/Analytics";
import type { UseAppMonitor } from "@/services/AppMonitor";
import useAppMonitor from "@/services/AppMonitor";
import { useLazyGetZoomUrlQuery, ZoomMeetingType } from "@/store/api/zoom";
import { useAppUserSelector } from "@/store/reducers/appUser";
import { selectWaitingRoomData } from "@/store/reducers/modals/waitingRoomModal";
import { Button, FAIconName, FloatingButton } from "@/ui/core";
import type { ButtonSize, ButtonVariant } from "@/ui/core/buttons/Button";
import { getActorField } from "@/utils/analytics.util";
import { isEventWithin15Minutes } from "@/utils/date.util";
import { openUrl } from "@/utils/navigation.util";
import { joinTestIDs } from "@/utils/string.util";

type JoinZoomButtonLocation = "card" | "details" | "liveEvent" | "waitingRoom";

interface JoinZoomButtonProps {
  endDate: string;
  eventId: string;
  eventTitle: string;
  hasIcon?: boolean;
  isFloating?: boolean;
  isGroupClass?: boolean;
  isPhoneCallEvent?: boolean;
  location?: JoinZoomButtonLocation;
  patientId?: string;
  selectedUserId?: string;
  size?: ButtonSize;
  startDate: string;
  variant?: ButtonVariant;
}
interface AnalyticsInfo {
  eventName: string;
  source: string;
}

const getAnalyticsInfo = ({
  isGroupClass,
  location = "card",
}: {
  isGroupClass: boolean;
  location?: JoinZoomButtonLocation;
}): AnalyticsInfo => {
  if (location === "liveEvent") {
    return {
      eventName: ScheduleAnalyticsEvent.LiveEventJoinZoom,
      source: ScheduleAnalyticsSources.LiveEventView,
    };
  }
  if (location === "waitingRoom") {
    return {
      eventName: WaitingRoomAnalyticsEvent.SkipAndJoin,
      source: ScheduleAnalyticsSources.WaitingRoom,
    };
  }
  if (location === "details") {
    return {
      eventName: isGroupClass
        ? ScheduleAnalyticsEvent.GroupEventDetailsJoinZoom
        : ScheduleAnalyticsEvent.AppointmentDetailsJoinZoom,
      source: isGroupClass
        ? ScheduleAnalyticsSources.GroupEventDetailView
        : ScheduleAnalyticsSources.AppointmentDetailView,
    };
  }
  return {
    eventName: isGroupClass
      ? ScheduleAnalyticsEvent.GroupEventCardJoinZoom
      : ScheduleAnalyticsEvent.AppointmentCardJoinZoom,
    source: ScheduleAnalyticsSources.Card,
  };
};

const JoinZoomTitle = "Join Zoom";
const SkipAndJoinTitle = "Skip and Join Now";

const shouldBlockButton = ({
  isPhoneCallEvent,
  isWithin15Minutes,
  location,
}: {
  isPhoneCallEvent: boolean;
  isWithin15Minutes: boolean;
  location: JoinZoomButtonLocation;
}): boolean => {
  if (isPhoneCallEvent) return true;
  if (location === "liveEvent" || location === "waitingRoom") return false;
  return !isWithin15Minutes;
};

const shouldHideForSupportUser = ({
  appUserExternalId,
  isAppUserSupport,
  selectedUserId,
}: {
  appUserExternalId: string;
  isAppUserSupport: boolean;
  selectedUserId?: string;
}): boolean => {
  if (!isAppUserSupport) return false;
  if (!selectedUserId) return false;
  return selectedUserId !== appUserExternalId;
};

const getButtonTitle = (location: JoinZoomButtonLocation): string =>
  location === "waitingRoom" ? SkipAndJoinTitle : JoinZoomTitle;

const useJoinZoomHandlers = ({
  captureException,
  dismissWaitingRoom,
  location,
  skipWaitingRoom,
}: {
  captureException: UseAppMonitor["captureException"];
  dismissWaitingRoom: () => void;
  location: JoinZoomButtonLocation;
  skipWaitingRoom: () => Promise<void>;
}) => {
  const handleJoinZoomError = useCallback(() => {
    const onPress = location === "waitingRoom" ? dismissWaitingRoom : undefined;
    Alert.alert("Cannot Join", "Cannot open Zoom meeting, please try again", [
      { onPress, text: "Ok" },
    ]);
  }, [dismissWaitingRoom, location]);

  const openZoomMeeting = useCallback(
    async (meetingUrl?: string) => {
      if (!meetingUrl || meetingUrl.includes("null")) {
        captureException("Couldn't join zoom meeting. No meeting URL provided");
        handleJoinZoomError();
        return;
      }
      await openUrl({
        onError: (error?: Error) => {
          captureException(
            new Error("Couldn't join zoom meeting. Cannot open URL", {
              cause: error,
            }),
          );
          handleJoinZoomError();
        },
        url: meetingUrl,
      });
    },
    [captureException, handleJoinZoomError],
  );

  const handleSkipWaitingRoom = useCallback(async () => {
    await skipWaitingRoom()
      .catch((error) => {
        captureException(
          new Error("Failed to skip waiting room assessment", { cause: error }),
        );
      })
      .finally(() => {
        dismissWaitingRoom();
      });
  }, [captureException, dismissWaitingRoom, skipWaitingRoom]);

  return { handleJoinZoomError, handleSkipWaitingRoom, openZoomMeeting };
};

const JoinZoomButton = ({
  endDate,
  eventId,
  eventTitle,
  hasIcon,
  isFloating,
  isGroupClass,
  isPhoneCallEvent = false,
  location = "card",
  patientId,
  selectedUserId,
  size = "small",
  startDate,
  variant = "primary",
}: JoinZoomButtonProps) => {
  const { captureException } = useAppMonitor();
  const { track } = useAnalytics();
  const { isGroupClassRedesignEnabled } = useFeatureFlags();
  const [getZoomUrl] = useLazyGetZoomUrlQuery();
  const [isLoading, setIsLoading] = useState(false);
  const { dismissWaitingRoom, interceptJoinPress, skipWaitingRoom } =
    useWaitingRoomIntercept();
  const { externalId: appUserExternalId, isSupport: isAppUserSupport } =
    useAppUserSelector();
  const waitingRoomData = useSelector(selectWaitingRoomData);
  const baseTestID = joinTestIDs(
    ScheduleTestID.ScheduleEventCard,
    ScheduleTestID.JoinZoom,
  );

  const { handleJoinZoomError, handleSkipWaitingRoom, openZoomMeeting } =
    useJoinZoomHandlers({
      captureException,
      dismissWaitingRoom,
      location,
      skipWaitingRoom,
    });

  const isWithin15Minutes = isEventWithin15Minutes({
    endDate,
    startDate,
  });

  const trackJoinEvent = useCallback(() => {
    const { eventName, source } = getAnalyticsInfo({
      isGroupClass: Boolean(isGroupClass),
      location,
    });
    const actorField = getActorField({
      appUserExternalId,
      isAppUserSupport,
    });
    track(eventName, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.EventId]: eventId,
      [AnalyticsFieldName.EventTitle]: eventTitle,
      [AnalyticsFieldName.Source]: source,
      [ScheduleAnalyticsFieldName.AppointmentExternalId]: eventId,
      [ScheduleAnalyticsFieldName.ResponseExternalId]:
        waitingRoomData?.assessmentId,
      ...actorField,
    });
  }, [
    appUserExternalId,
    eventId,
    eventTitle,
    isAppUserSupport,
    isGroupClass,
    location,
    track,
    waitingRoomData?.assessmentId,
  ]);

  const handlePress = useCallback(async () => {
    setIsLoading(true);
    const isAlreadyWaitingRoom = location === "waitingRoom";
    try {
      // Check waiting room intercept for non-waiting-room locations
      const canProceed = isAlreadyWaitingRoom
        ? true
        : await interceptJoinPress({
            endDate,
            eventId,
            eventTitle,
            isGroupClass,
            patientId: patientId ?? appUserExternalId,
            startDate,
          });

      if (!canProceed) return;

      if (isAlreadyWaitingRoom) {
        await handleSkipWaitingRoom();
      }

      trackJoinEvent();

      const result = await getZoomUrl({
        externalId: eventId,
        isGroupClassRedesignEnabled,
        type: isGroupClass
          ? ZoomMeetingType.GroupClassEvent
          : ZoomMeetingType.AppointmentEvent,
      });
      const url = isNil(result) ? undefined : result.data?.joinUrl;

      await openZoomMeeting(url);
    } catch (error) {
      captureException(
        new Error("Couldn't join zoom meeting. Error getting URL", {
          cause: error,
        }),
      );
      handleJoinZoomError();
    } finally {
      setIsLoading(false);
    }
  }, [
    appUserExternalId,
    captureException,
    endDate,
    eventId,
    eventTitle,
    getZoomUrl,
    handleJoinZoomError,
    handleSkipWaitingRoom,
    interceptJoinPress,
    isGroupClass,
    isGroupClassRedesignEnabled,
    location,
    openZoomMeeting,
    patientId,
    startDate,
    trackJoinEvent,
  ]);

  const shouldHide = shouldHideForSupportUser({
    appUserExternalId,
    isAppUserSupport,
    selectedUserId,
  });

  if (shouldHide) {
    return null;
  }

  if (shouldBlockButton({ isPhoneCallEvent, isWithin15Minutes, location })) {
    return null;
  }

  const buttonTitle = getButtonTitle(location);
  const leftIcon = hasIcon ? FAIconName.ExternalLink : undefined;

  if (isFloating) {
    return (
      <FloatingButton
        accessibilityLabel={buttonTitle}
        isLoading={isLoading}
        leftIcon={leftIcon}
        onPress={handlePress}
        testID={baseTestID}
        title={buttonTitle}
      />
    );
  }

  return (
    <Button
      accessibilityLabel={buttonTitle}
      isLoading={isLoading}
      leftIcon={leftIcon}
      onPress={handlePress}
      size={size}
      testID={baseTestID}
      title={buttonTitle}
      variant={variant}
    />
  );
};

export default JoinZoomButton;
